import React, { useState } from 'react';
import { Star, MapPin, Clock, MessageCircle, Calendar, CheckCircle } from 'lucide-react';
import { PAGE_CHROME_LIGHT } from '../constants/pageChrome';
import { usePageStatusBar } from '../hooks/usePageStatusBar';

export const PersonalTrainers = () => {
  usePageStatusBar({ background: PAGE_CHROME_LIGHT });
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');

  const trainers = [
    {
      id: 1,
      name: 'Sarah Johnson',
      specialty: 'weight-loss',
      location: 'New York, NY',
      rating: 4.9,
      reviews: 127,
      experience: '8 years',
      rate: 75,
      avatar: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=150&h=150&fit=crop&crop=face&auto=format',
      bio: 'Certified personal trainer specializing in weight loss and body transformation. Helped over 500 clients achieve their fitness goals.',
      specialties: ['Weight Loss', 'HIIT', 'Nutrition Coaching', 'Body Transformation'],
      availability: 'Mon-Fri: 6AM-8PM, Sat: 8AM-4PM',
      certifications: ['NASM-CPT', 'Precision Nutrition', 'TRX Certified']
    },
    {
      id: 2,
      name: 'Mike Rodriguez',
      specialty: 'strength',
      location: 'Los Angeles, CA',
      rating: 4.8,
      reviews: 89,
      experience: '6 years',
      rate: 85,
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face&auto=format',
      bio: 'Former competitive powerlifter with expertise in strength training and muscle building for all fitness levels.',
      specialties: ['Strength Training', 'Powerlifting', 'Muscle Building', 'Athletic Performance'],
      availability: 'Mon-Sat: 5AM-9PM',
      certifications: ['CSCS', 'USAPL Coach', 'FMS Level 2']
    },
    {
      id: 3,
      name: 'Emily Chen',
      specialty: 'yoga',
      location: 'San Francisco, CA',
      rating: 4.9,
      reviews: 156,
      experience: '10 years',
      rate: 65,
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face&auto=format',
      bio: 'Yoga instructor and mindfulness coach helping clients find balance between physical fitness and mental wellness.',
      specialties: ['Yoga', 'Pilates', 'Mindfulness', 'Flexibility Training'],
      availability: 'Tue-Sun: 7AM-7PM',
      certifications: ['RYT-500', 'Pilates Certified', 'Meditation Teacher']
    },
    {
      id: 4,
      name: 'David Thompson',
      specialty: 'cardio',
      location: 'Chicago, IL',
      rating: 4.7,
      reviews: 73,
      experience: '5 years',
      rate: 70,
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face&auto=format',
      bio: 'Marathon runner and endurance coach specializing in cardiovascular fitness and running performance.',
      specialties: ['Running', 'Cardio Training', 'Marathon Prep', 'Endurance Building'],
      availability: 'Mon-Fri: 6AM-6PM, Sun: 8AM-2PM',
      certifications: ['RRCA Certified', 'USATF Level 1', 'CPR/AED']
    },
    {
      id: 5,
      name: 'Jessica Martinez',
      specialty: 'rehabilitation',
      location: 'Miami, FL',
      rating: 4.8,
      reviews: 94,
      experience: '7 years',
      rate: 80,
      avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face&auto=format',
      bio: 'Physical therapist and corrective exercise specialist focusing on injury prevention and rehabilitation.',
      specialties: ['Injury Rehabilitation', 'Corrective Exercise', 'Post-PT Training', 'Mobility'],
      availability: 'Mon-Thu: 8AM-6PM, Fri: 8AM-4PM',
      certifications: ['DPT', 'NASM-CES', 'FMS Certified']
    },
    {
      id: 6,
      name: 'Alex Kim',
      specialty: 'functional',
      location: 'Seattle, WA',
      rating: 4.9,
      reviews: 112,
      experience: '9 years',
      rate: 90,
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face&auto=format',
      bio: 'Functional movement specialist and former CrossFit athlete. Expert in compound movements and athletic performance.',
      specialties: ['Functional Training', 'CrossFit', 'Olympic Lifting', 'Athletic Performance'],
      availability: 'Mon-Sat: 5AM-8PM',
      certifications: ['CF-L3', 'USAW Sport Performance', 'FMS Level 2']
    }
  ];

  const specialties = [
    { id: 'all', label: 'All Trainers' },
    { id: 'weight-loss', label: 'Weight Loss' },
    { id: 'strength', label: 'Strength Training' },
    { id: 'yoga', label: 'Yoga & Pilates' },
    { id: 'cardio', label: 'Cardio & Endurance' },
    { id: 'rehabilitation', label: 'Rehabilitation' },
    { id: 'functional', label: 'Functional Training' }
  ];

  const filteredTrainers = selectedSpecialty === 'all' 
    ? trainers 
    : trainers.filter(trainer => trainer.specialty === selectedSpecialty);

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Personal Trainers
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Connect with certified personal trainers who can provide expert guidance tailored to your specific fitness goals.
          </p>
        </div>

        {/* Specialty Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {specialties.map((specialty) => (
              <button
                key={specialty.id}
                onClick={() => setSelectedSpecialty(specialty.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  selectedSpecialty === specialty.id
                    ? 'bg-[var(--action-primary)] text-[var(--ink)]'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {specialty.label}
              </button>
            ))}
          </div>
        </div>

        {/* Trainers Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTrainers.map((trainer) => (
            <div key={trainer.id} className="card hover:shadow-xl transition-shadow duration-300 flex flex-col h-full">
              <div className="flex items-center mb-4">
                <img
                  src={trainer.avatar}
                  alt={trainer.name}
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {trainer.name}
                  </h3>
                  <div className="flex items-center text-sm text-gray-500">
                    <MapPin className="w-4 h-4 mr-1" />
                    {trainer.location}
                  </div>
                </div>
              </div>

              <div className="flex items-center mb-3">
                <div className="flex items-center">
                  <Star className="w-4 h-4 text-yellow-500 mr-1" />
                  <span className="font-medium text-gray-900">{trainer.rating}</span>
                  <span className="text-gray-500 ml-1">({trainer.reviews} reviews)</span>
                </div>
              </div>

              <p className="text-gray-600 mb-4 text-sm">
                {trainer.bio}
              </p>

              <div className="mb-4">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <Clock className="w-4 h-4 mr-1" />
                  {trainer.experience} experience
                </div>
                <div className="text-sm text-gray-500">
                  <strong className="text-gray-900">${trainer.rate}/session</strong>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Specialties:</h4>
                <div className="flex flex-wrap gap-1">
                  {trainer.specialties.map((specialty, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-full"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mb-6 flex-1">
                <h4 className="font-medium text-gray-900 mb-2">Certifications:</h4>
                <div className="space-y-1">
                  {trainer.certifications.map((cert, index) => (
                    <div key={index} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="w-3 h-3 text-green-500 mr-2" />
                      {cert}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 mt-auto">
                <button className="flex-1 btn-primary flex items-center justify-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Session
                </button>
                <button className="btn-secondary flex items-center justify-center px-3 py-3 min-w-[48px]">
                  <MessageCircle className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredTrainers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No trainers found for the selected specialty.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalTrainers; 